import { generatePath, Path } from 'react-router'
import { documentCodeParam, fileNameParam } from './pages/ConstructionDossierPage/query-params'
import { legendOpenParam, ViewMode, viewParam } from './pages/MapPage/query-params'
import { routing } from './routes'
import toSearchParams from './shared/utils/toSearchParams'
import { DataType, DataSubtype } from './pages/SearchPage/types'

export const ONE_DESIGN_ROUTES: Partial<Record<DataType, Partial<Record<DataSubtype, string>>>> = {
  [DataType.Bag]: {
    [DataSubtype.Nummeraanduiding]: routing.addressDetail.path,
    [DataSubtype.OpenbareRuimtes]: routing.openbareRuimteDetail.path,
    [DataSubtype.Panden]: routing.pandDetail.path,
    [DataSubtype.Woonplaats]: routing.woonplaatsDetail.path,
  },
  [DataType.BRK]: {
    [DataSubtype.Object]: routing.kadastraalObjectDetail.path,
    [DataSubtype.Subject]: routing.kadastraalSubjectDetail.path,
  },
  [DataType.Gebieden]: {
    [DataSubtype.BouwBlokken]: routing.bouwblokDetail.path,
    [DataSubtype.Buurten]: routing.buurtDetail.path,
    [DataSubtype.GGWGebieden]: routing.gebiedsGerichtWerkenDetail.path,
    [DataSubtype.StedelijkGebiedProjectOfBelangen]:
      routing.stedelijkGebiedProjectOfBelangenDetail.path,
    [DataSubtype.Stadsdelen]: routing.stadsdeelDetail.path,
    [DataSubtype.Wijken]: routing.wijkDetail.path,
  },
  [DataType.HR]: {
    [DataSubtype.Vestigingen]: routing.vestigingDetail.path,
    [DataSubtype.MaatschappelijkeActiviteiten]: routing.maDetail.path,
  },
  [DataType.Monumenten]: {
    [DataSubtype.Complexen]: routing.complexDetail.path,
    [DataSubtype.Monumenten]: routing.monumentDetail.path,
  },
}

export const toAddresses = (): Partial<Path> => ({
  pathname: routing.addresses.path,
  search: toSearchParams([[viewParam, ViewMode.Full]]).toString(),
})

export const toTablesPage = (): Partial<Path> => ({
  pathname: routing.tables.path,
})

export const toServicesPage = (): Partial<Path> => ({
  pathname: routing.services.path,
})

export const toCadastralObjects = (): Partial<Path> => ({
  pathname: routing.cadastralObjects.path,
  search: toSearchParams([[viewParam, ViewMode.Full]]).toString(),
})

export const toConstructionDossier = (
  id: string,
  fileName?: string,
  documentCode?: string,
): Partial<Path> => {
  const pathname = generatePath(routing.constructionDossier.path, { id })
  const searchParams = new URLSearchParams()

  if (fileName) {
    searchParams.set(fileNameParam.name, fileName)
  }

  if (documentCode) {
    searchParams.set(documentCodeParam.name, documentCode)
  }

  return { pathname, search: searchParams.toString() }
}

export interface DataDetailParams {
  type: DataType
  subtype: DataSubtype
  id: string
}

const toDataDetailLegacy = ({ type, subtype, id }: DataDetailParams): Partial<Path> => {
  return {
    pathname: generatePath(routing.dataDetail.path, {
      type,
      subtype,
      id,
    }),
    search: window.location.search,
  }
}

const toDataDetailOneDesign = ({ type, subtype, id }: DataDetailParams): Partial<Path> => {
  const oneDesignRoute = ONE_DESIGN_ROUTES[type]?.[subtype]

  if (oneDesignRoute) {
    return {
      pathname: generatePath(oneDesignRoute, {
        type,
        subtype: subtype === DataSubtype.GGWGebieden ? 'gebiedsgerichtwerken' : subtype,
        id,
      }),
      search: window.location.search,
    }
  }

  return toDataDetailLegacy({ type, subtype, id })
}

export const toDataDetail = (params: DataDetailParams): Partial<Path> => {
  return toDataDetailOneDesign(params)
}

export const toEstablishments = (): Partial<Path> => ({
  pathname: routing.establishments.path,
  search: toSearchParams([[viewParam, ViewMode.Full]]).toString(),
})

// TODO: Rename this method to match the name of the route.
export const toGeoSearch = (): Partial<Path> => ({
  pathname: routing.dataSearchGeo.path,
})

export const toHome = (): Partial<Path> => ({
  pathname: routing.home.path,
})

export const toMapSearch = (): Partial<Path> => ({
  pathname: routing.mapSearch.path,
  search: toSearchParams([[legendOpenParam, true]]).toString(),
})

export const toNotFound = (): Partial<Path> => ({
  pathname: routing.notFound.path,
})

export const toSearch = (): Partial<Path> => ({
  pathname: routing.search.path,
})

export const toConstructionDossierList = (): Partial<Path> => ({
  pathname: routing.constructionDossierList.path,
})

export const toCatalogDetail = (id: string): Partial<Path> => {
  const encodedId = encodeURIComponent(id)
  return {
    pathname: generatePath(routing.catalogDetail.path, { id: encodedId }),
  }
}
